import { Participant, ParticipantWithId } from "../interfaces/IParticipants";
import {
  createParticipant,
  updateParticipant as updateParticipantInFirestore,
} from "../utils/firestore";

// Interfejs dla wyniku operacji
interface OperationResult {
  success: boolean;
  error?: string;
  participant?: ParticipantWithId;
}

/**
 * Hook do operacji CRUD na zawodnikach
 * Zgodny z zasadą pojedynczej odpowiedzialności - tylko operacje na Firestore
 */
export const useParticipantOperations = (userId: string) => {
  /**
   * Tworzy nowego zawodnika w Firestore
   */
  const performAdd = async (participantData: Participant): Promise<OperationResult> => {
    try {
      const createdParticipant = await createParticipant(participantData, userId);
      return { success: true, participant: createdParticipant };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Aktualizuje zawodnika w Firestore
   * Przyjmuje pełny obiekt zawodnika (lub częściowe dane z ID)
   */
  const performUpdate = async (
    participantId: string,
    updatedData: Partial<ParticipantWithId> | ParticipantWithId
  ): Promise<OperationResult> => {
    try {
      if (!participantId) {
        return { success: false, error: "ID zawodnika jest wymagane" };
      }

      // Tworzymy obiekt zawodnika z ID i danymi do aktualizacji
      const participantToUpdate = { id: participantId, ...updatedData } as ParticipantWithId;

      await updateParticipantInFirestore(participantToUpdate, userId);
      return { success: true, participant: participantToUpdate };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Wykonuje soft delete zawodnika (zmienia flagę isActive na false)
   */
  const performDelete = async (participantId: string): Promise<OperationResult> => {
    try {
      if (!participantId) {
        return { success: false, error: "ID zawodnika jest wymagane" };
      }

      // Soft delete - zmień flagę isActive na false
      const deactivatedParticipant = {
        id: participantId,
        isActive: false,
      } as Partial<ParticipantWithId>;
      await updateParticipantInFirestore(deactivatedParticipant as ParticipantWithId, userId);

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return { success: false, error: errorMessage };
    }
  };

  return {
    performAdd,
    performUpdate,
    performDelete,
  };
};
